import React, { useRef, useCallback, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InspectionGridItem, ItemDetail } from "../types";
import { MessageSquare } from "lucide-react";

interface DetailInspectionModalProps {
  show: boolean;
  isDarkMode: boolean;
  selectedInspection: InspectionGridItem | null;
  savingResponse: number | null;
  onClose: () => void;
  onTabChange: (tabIndex: number) => void;
  onSaveResponse: (
    sheetId: number,
    itemId: number,
    response: string | null,
    notes?: string
  ) => void;
  onUpdateNotes: (sheetId: number, itemId: number, notes: string) => void;
}

export const DetailInspectionModal: React.FC<DetailInspectionModalProps> = ({
  show,
  isDarkMode,
  selectedInspection,
  savingResponse,
  onClose,
  onTabChange,
  onSaveResponse,
  onUpdateNotes,
}) => {
  // Local state para las notas mientras el usuario escribe
  const [localNotes, setLocalNotes] = useState<Record<number, string>>({});
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});
  const isEditingRef = useRef<Record<number, boolean>>({});

  // Handler con debounce para actualizar notas
  const handleUpdateNotes = useCallback(
    (itemId: number, notes: string) => {
      // Marcar que el usuario está editando este item
      isEditingRef.current[itemId] = true;

      // Actualizar estado local inmediatamente (no pierde foco)
      setLocalNotes((prev) => ({ ...prev, [itemId]: notes }));

      // Limpiar timeout anterior
      if (debounceTimeouts.current[itemId]) {
        clearTimeout(debounceTimeouts.current[itemId]);
      }

      // Crear nuevo timeout para guardar después de 3 segundos
      debounceTimeouts.current[itemId] = setTimeout(() => {
        if (selectedInspection) {
          onUpdateNotes(
            selectedInspection.sheetDetails[selectedInspection.activeTab].id,
            itemId,
            notes
          );
        }
        // Marcar como no editando después de guardar
        setTimeout(() => {
          isEditingRef.current[itemId] = false;
        }, 100);
      }, 3000); // 3 segundos de debounce para usuarios lentos
    },
    [onUpdateNotes, selectedInspection]
  );

  // Obtener el valor actual de las notas (local o del servidor)
  const getNotesValue = (item: ItemDetail) => {
    return localNotes[item.id] !== undefined
      ? localNotes[item.id]
      : item.notes || "";
  };

  // Inicializar localNotes cuando cambia la inspección
  useEffect(() => {
    if (selectedInspection) {
      const initialNotes: Record<number, string> = {};
      selectedInspection.sheetDetails.forEach((sheet) => {
        sheet.items.forEach((item) => {
          // Solo actualizar si el usuario NO está editando este item
          if (item.notes && !isEditingRef.current[item.id]) {
            initialNotes[item.id] = item.notes;
          }
        });
      });
      setLocalNotes((prev) => {
        // Mantener las notas que el usuario está editando
        const merged = { ...initialNotes };
        Object.keys(prev).forEach((key) => {
          const itemId = parseInt(key);
          if (isEditingRef.current[itemId]) {
            merged[itemId] = prev[itemId];
          }
        });
        return merged;
      });
    }
  }, [selectedInspection?.inspection.id, selectedInspection?.sheetDetails]);

  // Limpiar timeouts al cerrar el modal
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  if (!selectedInspection) return null;

  // Group items by section
  const groupedItems = selectedInspection.sheetDetails[
    selectedInspection.activeTab
  ].items.reduce((acc: Record<string, ItemDetail[]>, item: ItemDetail) => {
    if (!acc[item.section_name]) {
      acc[item.section_name] = [];
    }
    acc[item.section_name].push(item);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex flex-col h-full overflow-hidden">
              {/* Header */}
              <div
                className="px-6 py-5 border-b"
                style={{
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  borderBottomColor: isDarkMode ? "#374151" : "#e5e7eb",
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {selectedInspection.inspection.vehicle_number}
                    </h3>
                    <p className="text-red-100 text-sm mt-1">
                      {selectedInspection.inspection.vehicle_make}{" "}
                      {selectedInspection.inspection.vehicle_model}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onClose}
                      className="text-white hover:text-red-200 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div
                className={`flex border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {selectedInspection.sheetDetails.map((sheet, index) => (
                  <button
                    key={sheet.id}
                    onClick={() => onTabChange(index)}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      selectedInspection.activeTab === index
                        ? isDarkMode
                          ? "bg-gray-700 text-red-400 border-b-2 border-red-400"
                          : "bg-gray-50 text-red-600 border-b-2 border-red-600"
                        : isDarkMode
                        ? "text-gray-400 hover:text-gray-300 hover:bg-gray-700/50"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <div>
                      {sheet.template_name.split("_")[0].replace(/_/g, " ")}
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      {sheet.type === "PRETRIP_GENERAL"
                        ? "Pre-Trip"
                        : "DOT Inspection"}
                    </div>
                  </button>
                ))}
              </div>

              {/* Content */}
              <div
                className="flex-1 overflow-y-auto p-6"
                style={{ scrollBehavior: "auto" }}
              >
                {selectedInspection.sheetDetails[selectedInspection.activeTab]
                  .items.length === 0 ? (
                  <div className="text-center py-12">
                    <p
                      className={`text-lg ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No items in this sheet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedItems).map(
                      ([sectionName, items]) => (
                        <div key={sectionName}>
                          <h4
                            className={`text-sm font-semibold mb-3 pb-2 border-b ${
                              isDarkMode
                                ? "text-gray-300 border-gray-600"
                                : "text-red-600 border-red-300"
                            }`}
                          >
                            {sectionName}
                          </h4>
                          <div className="space-y-2">
                            {items.map((item: ItemDetail) => (
                              <div
                                key={item.id}
                                className={`p-3 rounded-lg border ${
                                  isDarkMode
                                    ? "bg-gray-700 border-gray-600"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div
                                      className={`font-medium ${
                                        isDarkMode
                                          ? "text-gray-200"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      {item.item_name}
                                    </div>
                                  </div>

                                  {/* Botones de respuesta - diferentes según el tipo */}
                                  {selectedInspection.sheetDetails[
                                    selectedInspection.activeTab
                                  ].type === "PRETRIP_GENERAL" ? (
                                    // PRETRIP: Pass/Fail/NA buttons
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() =>
                                          onSaveResponse(
                                            selectedInspection.sheetDetails[
                                              selectedInspection.activeTab
                                            ].id,
                                            item.id,
                                            item.response === "pass"
                                              ? null
                                              : "pass",
                                            item.notes
                                          )
                                        }
                                        disabled={savingResponse === item.id}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                                          item.response === "pass"
                                            ? "bg-green-500 text-white ring-2 ring-green-400"
                                            : isDarkMode
                                            ? "bg-gray-600 text-gray-200 hover:bg-green-500"
                                            : "bg-gray-200 text-gray-700 hover:bg-green-500 hover:text-white"
                                        }`}
                                      >
                                        ✓ Pass
                                      </button>
                                      <button
                                        onClick={() =>
                                          onSaveResponse(
                                            selectedInspection.sheetDetails[
                                              selectedInspection.activeTab
                                            ].id,
                                            item.id,
                                            item.response === "fail"
                                              ? null
                                              : "fail",
                                            item.notes
                                          )
                                        }
                                        disabled={savingResponse === item.id}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                                          item.response === "fail"
                                            ? "bg-red-500 text-white ring-2 ring-red-400"
                                            : isDarkMode
                                            ? "bg-gray-600 text-gray-200 hover:bg-red-500"
                                            : "bg-gray-200 text-gray-700 hover:bg-red-500 hover:text-white"
                                        }`}
                                      >
                                        ✗ Fail
                                      </button>
                                      <button
                                        onClick={() =>
                                          onSaveResponse(
                                            selectedInspection.sheetDetails[
                                              selectedInspection.activeTab
                                            ].id,
                                            item.id,
                                            item.response === "na"
                                              ? null
                                              : "na",
                                            item.notes
                                          )
                                        }
                                        disabled={savingResponse === item.id}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                                          item.response === "na"
                                            ? "bg-gray-500 text-white ring-2 ring-gray-400"
                                            : isDarkMode
                                            ? "bg-gray-600 text-gray-200 hover:bg-gray-500"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-500 hover:text-white"
                                        }`}
                                      >
                                        N/A
                                      </button>
                                    </div>
                                  ) : (
                                    // DOT: Checkbox (checked/unchecked)
                                    <div className="flex items-center gap-3">
                                      <label className="flex items-center cursor-pointer group">
                                        <input
                                          type="checkbox"
                                          checked={item.response === "checked"}
                                          onChange={(e) =>
                                            onSaveResponse(
                                              selectedInspection.sheetDetails[
                                                selectedInspection.activeTab
                                              ].id,
                                              item.id,
                                              e.target.checked
                                                ? "checked"
                                                : null,
                                              item.notes
                                            )
                                          }
                                          disabled={savingResponse === item.id}
                                          className="w-6 h-6 rounded border-2 border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 cursor-pointer"
                                        />
                                        <span
                                          className={`ml-2 text-sm font-medium ${
                                            item.response === "pass"
                                              ? isDarkMode
                                                ? "text-green-400"
                                                : "text-green-600"
                                              : isDarkMode
                                              ? "text-gray-400"
                                              : "text-gray-500"
                                          }`}
                                        >
                                          {item.response === "pass"
                                            ? "Checked"
                                            : "Check"}
                                        </span>
                                      </label>
                                    </div>
                                  )}
                                </div>

                                {/* Mostrar nota existente si hay */}
                                {item.notes && !item.response && (
                                  <div className="mt-2 flex items-start gap-2 p-2 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                    <MessageSquare
                                      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                                        isDarkMode
                                          ? "text-blue-400"
                                          : "text-blue-600"
                                      }`}
                                    />
                                    <p
                                      className={`text-xs flex-1 ${
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      {item.notes}
                                    </p>
                                  </div>
                                )}

                                {/* Input de comentario inline - aparece cuando hay una respuesta */}
                                {item.response && (
                                  <div className="mt-3 space-y-2 border-t pt-3 border-gray-300 dark:border-gray-600">
                                    <div className="flex items-center gap-2">
                                      <MessageSquare
                                        className={`w-4 h-4 ${
                                          isDarkMode
                                            ? "text-blue-400"
                                            : "text-blue-600"
                                        }`}
                                      />
                                      <label
                                        className={`text-xs font-medium ${
                                          isDarkMode
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Note (optional)
                                      </label>
                                    </div>
                                    <textarea
                                      value={getNotesValue(item)}
                                      onChange={(e) =>
                                        handleUpdateNotes(
                                          item.id,
                                          e.target.value
                                        )
                                      }
                                      placeholder="Add observations or details..."
                                      className={`w-full px-3 py-2 text-sm rounded-md border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none ${
                                        isDarkMode
                                          ? "bg-gray-600 border-gray-500 text-white placeholder-gray-400"
                                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                                      }`}
                                      rows={2}
                                      disabled={savingResponse === item.id}
                                    />
                                  </div>
                                )}

                                {savingResponse === item.id && (
                                  <div className="mt-2 flex items-center gap-2 text-sm text-red-500">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                                    Guardando...
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
